import ENV from "../env";
import { buildHeaders, handleExpiredLogin } from "./utils";

import type { ValidationError } from "./validation-error";

/** Mirrors the backend's UserProfileResponse. */
export interface UserProfile {
    id: string;
    name: string;
    email: string | null;
    tester: boolean;
    premium: boolean;
}

/** Mirrors the backend's UserRegisterResponse. */
export interface UserRegistration {
    id: string;
    userAlreadyExists: boolean;
}

export default class ProfileService {
    static getProfile(workoutId: string): Promise<UserProfile> {
        return fetch(`${ ENV.BACKEND }/profile/${ workoutId }`, {
            method: 'GET',
            headers: buildHeaders()
        }).then((response) => handleExpiredLogin(response).json());
    }

    static getSelf(): Promise<UserProfile> {
        return fetch(`${ ENV.BACKEND }/profile/me`, {
            method: 'GET',
            headers: buildHeaders()
        }).then((response) => handleExpiredLogin(response).json());
    }

    static update(command: ProfileUpdateCommand,
                  successAction: (profile: UserProfile) => void,
                  validationFailAction: (errors: ValidationError[]) => void,
                  errorAction: () => void): void {

        fetch(`${ ENV.BACKEND }/profile/update`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify(command)
        }).then((response: Response): void => {
            handleExpiredLogin(response);

            if (response.status === 200) {
                response.json().then(successAction);
                return;
            }

            if (response.status === 400) {
                response.json().then(validationFailAction);
                return;
            }

            errorAction()
        }).catch(errorAction);
    }

    static register(command: ProfileRegisterCommand,
                    successAction: (registration: UserRegistration) => void,
                    validationFailAction: (errors: ValidationError[]) => void,
                    errorAction: () => void): void {

        fetch(`${ ENV.BACKEND }/profile/register`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify(command)
        }).then((response: Response): void => {
            handleExpiredLogin(response);

            if (response.status === 200) {
                response.json().then(successAction);
                return;
            }

            if (response.status === 400) {
                response.json().then(validationFailAction);
                return;
            }

            errorAction()
        }).catch(errorAction);
    }
}

export interface ProfileRegisterCommand {
    id: string;
    name: string;
    email: string;
    password: string;
}

/**
 * Fields are nullable on purpose: the backend skips @Size/@Email validation for null but
 * rejects an empty string, so untouched fields must be sent as null rather than "".
 */
export interface ProfileUpdateCommand {
    id: string;
    name: string | null;
    email: string | null;
    password: string | null;
}
